import axios from 'axios';
import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  REGISTER_REQUEST,
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  LOGOUT,
  AUTH_DEVICE_BANNED,
} from '../types/auth';

import { DOMAIN } from '../constants';
import getDeviceId from '../../utils/deviceId';
import buildAuthHeaders from '../../utils/apiHeaders';

// Login/register send X-Device-Id so the backend can adopt any anon votes
// cast from this device into the now-authenticated account. Without this
// header, votes made before login stay orphaned as deviceId-only rows.
// Delegates to utils/apiHeaders.js (single source of truth). Anonymous
// call - no userInfo - so no Authorization header, just the device id the
// backend needs for anon-vote adoption.
const authHeaders = () => buildAuthHeaders(null).headers;

export const login = (username, password) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_REQUEST });

    const config = { headers: authHeaders() };
    const { data } = await axios.post(`${DOMAIN}/api/v1/accounts/login/`, { username, password }, config);

    dispatch({ type: LOGIN_SUCCESS, payload: data });
    localStorage.setItem('userInfo', JSON.stringify(data));
  } catch (error) {
    dispatch({
      type: LOGIN_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
    });
  }
};

export const register = (username, password, extras = {}) => async (dispatch) => {
  try {
    dispatch({ type: REGISTER_REQUEST });

    const body = { username, password };
    if (extras.birthYear) body.birth_year = Number(extras.birthYear);
    if (extras.gender) body.gender = extras.gender;
    const config = { headers: authHeaders() };
    const { data } = await axios.post(`${DOMAIN}/api/v1/accounts/register/`, body, config);

    dispatch({ type: REGISTER_SUCCESS, payload: data });
    dispatch({ type: LOGIN_SUCCESS, payload: data });
    localStorage.setItem('userInfo', JSON.stringify(data));
  } catch (error) {
    dispatch({
      type: REGISTER_FAIL,
      payload: error.response && error.response.data.detail ? error.response.data.detail : error.message,
    });
  }
};

export const logout = () => (dispatch) => {
  localStorage.removeItem('userInfo');
  localStorage.removeItem('auth_blocked');
  dispatch({ type: LOGOUT });
};

// Coalesces concurrent calls so the App.js mount and the axios-interceptor
// retry don't both POST /anon/. Exposed via getPendingAnonPromise so
// store.js can await it without re-importing the action factory and
// risking a circular dependency.
let pendingAnonPromise = null;

export const getPendingAnonPromise = () => pendingAnonPromise;

export const loadOrCreateAnon = () => async (dispatch) => {
  // Already have an identity? Skip — applies to both anon and claimed users.
  let existing = null;
  try {
    existing = JSON.parse(localStorage.getItem('userInfo') || 'null');
  } catch (_) {
    /* ignore */
  }
  if (existing?.access) return existing;

  if (pendingAnonPromise) return pendingAnonPromise;

  pendingAnonPromise = (async () => {
    try {
      const { data } = await axios.post(
        `${DOMAIN}/api/v1/accounts/anon/`,
        {},
        { headers: { 'X-Device-Id': getDeviceId() } }
      );
      try {
        localStorage.setItem('userInfo', JSON.stringify(data));
      } catch (_) {
        /* ignore */
      }
      dispatch({ type: LOGIN_SUCCESS, payload: data });
      return data;
    } catch (err) {
      if (err?.response?.status === 403) {
        try {
          localStorage.setItem('auth_blocked', '1');
        } catch (_) {
          /* ignore */
        }
        dispatch({
          type: AUTH_DEVICE_BANNED,
          payload: (err?.response?.data && err.response.data.detail) || 'Device blocked',
        });
      }
      // Non-403 transient errors are silent — user can still browse via
      // deviceId-only Vote endpoints. The next user action that needs
      // auth will re-trigger via the axios interceptor.
      return null;
    } finally {
      pendingAnonPromise = null;
    }
  })();

  return pendingAnonPromise;
};
